import { useState } from "react";
import { motion } from "framer-motion";
import { Tent, ShoppingBasket, Landmark, Sun, Car, Castle, Blocks, Coffee, ArrowUpRight, CalendarHeart, MapPin } from "lucide-react";
import { Reveal } from "../components/Chapter";
import { SEJOUR_IDEAL, SEJOUR_MAPS, BOOKING_URL } from "../lib/data";

const ICONS = {
  tent: Tent,
  basket: ShoppingBasket,
  landmark: Landmark,
  sun: Sun,
  car: Car,
  castle: Castle,
  blocks: Blocks,
  coffee: Coffee,
};

export default function SejourIdealPage() {
  const [selected, setSelected] = useState(0);

  const showOnMap = (i) => {
    setSelected(i);
    document.getElementById("sejour-map")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <main data-testid="page-sejour-ideal" className="grain">
      <section className="relative pt-40 pb-14 px-6 sm:px-10 lg:px-16 bg-cream overflow-hidden">
        <CalendarHeart className="absolute -right-8 top-20 w-64 h-64 text-pine/5 rotate-12" />
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="font-body text-xs font-bold tracking-[0.25em] uppercase text-terra">
              Récit de séjour · Une semaine type
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-5 font-display font-black text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-ink max-w-4xl">
              Mon séjour <span className="text-pine">idéal</span> au Lac aux Oiseaux
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-ink/65 text-base lg:text-lg leading-relaxed">
              Huit jours racontés jour par jour, d'un mercredi à l'autre : marchés, dolmens,
              grands sites de l'Aveyron, escapade dans le Lot et piscine entre deux aventures.
              De quoi imaginer vos propres vacances — chaque journée se situe sur la carte.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-8 flex flex-wrap gap-2" data-testid="sejour-badges">
              {["8 jours", "100 % Aveyron", "en famille"].map((b) => (
                <span key={b} className="rounded-full px-4 py-2 text-xs font-bold bg-sand border border-ink/10 text-ink/60">
                  {b}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-6 sm:px-10 lg:px-16 py-16 lg:py-20 bg-cream">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7 relative">
            <div className="absolute left-[27px] sm:left-[31px] top-4 bottom-4 w-px bg-pine/20" />
            <div className="space-y-8">
              {SEJOUR_IDEAL.map((d, i) => {
                const Icon = ICONS[d.icon];
                const active = selected === i;
                return (
                  <Reveal key={`${d.day}-${i}`} delay={0.04 * i}>
                    <article className="relative flex gap-5 sm:gap-7" data-testid={`sejour-day-${i}`}>
                      <div className="relative z-10 flex flex-col items-center">
                        <span
                          className={`grid place-items-center w-14 h-14 sm:w-16 sm:h-16 shrink-0 rounded-full shadow-lg transition-colors duration-300 ${
                            active ? "bg-terra text-sand shadow-terra/30" : "bg-pine text-sand shadow-pine/25"
                          }`}
                        >
                          <Icon size={22} />
                        </span>
                      </div>
                      <div
                        className={`flex-1 rounded-[1.75rem] bg-sand border p-6 sm:p-7 transition-all duration-300 hover:-translate-y-0.5 ${
                          active ? "border-terra/50 shadow-xl shadow-terra/10" : "border-ink/10 hover:border-pine/40 hover:shadow-xl hover:shadow-pine/10"
                        }`}
                      >
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="rounded-full bg-terra/15 text-terra px-3.5 py-1 text-[11px] font-bold tracking-[0.15em] uppercase">
                            {d.day}
                          </span>
                          <span className="text-xs font-semibold text-ink/35">Jour {i + 1}</span>
                        </div>
                        <h2 className="mt-3 font-display font-bold text-2xl text-ink leading-tight">{d.title}</h2>
                        <p className="mt-3 text-sm sm:text-base text-ink/65 leading-relaxed">{d.text}</p>
                        <button
                          onClick={() => showOnMap(i)}
                          data-testid={`sejour-map-btn-${i}`}
                          className={`mt-5 inline-flex items-center gap-2 rounded-full text-sm font-semibold px-5 py-2.5 transition-all duration-300 ${
                            active ? "bg-terra text-sand shadow-md shadow-terra/25" : "bg-cream border border-ink/10 text-ink/70 hover:border-pine/40 hover:text-pine"
                          }`}
                        >
                          <MapPin size={15} /> Voir sur la carte
                        </button>
                      </div>
                    </article>
                  </Reveal>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-5 lg:sticky lg:top-28 space-y-6">
            <Reveal delay={0.15}>
              <div id="sejour-map" className="rounded-[1.75rem] overflow-hidden border-8 border-sand shadow-2xl shadow-ink/15 bg-sand" data-testid="sejour-map-frame">
                <div className="px-5 pt-4 pb-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-ink/40">
                      {SEJOUR_IDEAL[selected].day} · Jour {selected + 1}
                    </div>
                    <div className="font-display font-bold text-lg text-ink truncate">{SEJOUR_IDEAL[selected].title}</div>
                  </div>
                  <span className="shrink-0 grid place-items-center w-10 h-10 rounded-full bg-pine text-sand">
                    <MapPin size={17} />
                  </span>
                </div>
                <motion.iframe
                  key={SEJOUR_MAPS[selected]}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  title={`Carte — ${SEJOUR_IDEAL[selected].title}`}
                  src={`https://www.google.com/maps?q=${encodeURIComponent(SEJOUR_MAPS[selected])}&z=12&output=embed`}
                  className="w-full h-80 sm:h-96 border-0"
                  loading="lazy"
                />
                <p className="px-5 py-3 text-xs text-ink/40">Destination principale de la journée — les étapes intermédiaires sont racontées dans le récit.</p>
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sejour-cta-booking"
                className="flex items-center justify-between gap-4 rounded-[1.75rem] bg-pine text-sand p-6 sm:p-8 transition-all duration-300 hover:bg-pinedark hover:-translate-y-1 hover:shadow-xl hover:shadow-pine/25"
              >
                <div>
                  <div className="font-display font-bold text-2xl sm:text-3xl">Écrivez votre propre semaine.</div>
                  <p className="mt-1.5 text-sm sm:text-base text-sand/70">
                    Le point de départ de toutes ces aventures : votre mobil-home au bord du lac.
                  </p>
                </div>
                <span className="grid place-items-center w-12 h-12 shrink-0 rounded-full bg-gold text-pinedark">
                  <ArrowUpRight size={20} />
                </span>
              </a>
            </Reveal>
          </div>
        </div>

        <div className="max-w-3xl mx-auto mt-14">
          <Reveal delay={0.25}>
            <p className="text-center font-hand text-3xl text-terra -rotate-1">
              et vous, votre mercredi d'arrivée, c'est pour quand ?
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
